import './styles.scss';
import React, { useCallback } from 'react';
import classNames from 'classnames';
// import { useSelector } from 'react-redux';
import Truncate from 'react-truncate';
import { useTracking } from 'react-tracking';
import { Badge, Card } from '@edx/paragon';
import { ListAlt, Person, Timelapse } from '@edx/paragon/icons';
// import { useIntl } from 'gatsby-plugin-react-intl';

import { addCloudflareImageParams, ImageOptions } from '@prospectus/common/ui/CloudflareImage/utils';
import ProspectusLink from '@prospectus/common/ui-strict/ProspectusLink';

import { submitEvent } from '@utils/analytics';
// import { formatPrice } from '@utils/formatting';

import FooterList from './components/FooterList';
// import StarRating from './components/StarRating';
import { ExpandedProductCardProps } from './types';

const CARD_HEADER_IMAGE_DARK = '/images/product_pages/default-card-header-dark.png';
const CARD_HEADER_IMAGE_LIGHT = '/images/product_pages/default-card-header-light.png';

const cloudflareImageParams: ImageOptions = { quality: 60, format: 'webp', width: 350 };

// gets the transformed url for cloudflare image
const getCloudflareTransformedURL = (
  imgSrc:string,
  options = cloudflareImageParams,
) => addCloudflareImageParams(imgSrc, options);

function ExpandedProductCard({
  url,
  cardAnalytics,
  customHeaderImage,
  schoolLogo,
  title,
  subtitle,
  variant,
  productTypeCopy,
  tags,
  // rating,
  // reviewCount,
  // minPrice,
  // maxPrice,
  length,
  learningType,
  courseCount,
  uuid,
  shouldfirePreQuerySegmentEvent,
  handleOnClick,
  isLoading = false,
}: ExpandedProductCardProps) {
  // @todo: reintroduce price and rating once that data is available for all products
  // const intl = useIntl();
  // const userCurrency = useSelector((state: { userCurrency: object }) => state.userCurrency);

  // const getPriceString = () => {
  //   const formattedMinPrice = typeof minPrice === 'number' ? formatPrice(minPrice, userCurrency, intl) : null;
  //   const formattedMaxPrice = typeof maxPrice === 'number' ? formatPrice(maxPrice, userCurrency, intl) : null;

  //   if (formattedMinPrice && formattedMaxPrice) {
  //     return `${formattedMinPrice} - ${formattedMaxPrice}`;
  //   }

  //   return formattedMinPrice || formattedMaxPrice;
  // };

  // const priceString = getPriceString();

  const getFooterList = () => {
    const footerList = [];

    if (length) {
      footerList.push({ iconSrc: Timelapse, text: length });
    }

    if (learningType) {
      footerList.push({ iconSrc: Person, text: learningType });
    }

    if (courseCount) {
      footerList.push({ iconSrc: ListAlt, text: `${courseCount} course${courseCount > 1 ? 's' : ''}` });
    }

    return footerList;
  };

  const DEFAULT_CARD_HEADER = variant === 'dark'
    ? CARD_HEADER_IMAGE_DARK
    : CARD_HEADER_IMAGE_LIGHT;
  const tracking = useTracking();
  // get the card header image source
  const getCardImgSrc = useCallback(
    () => (
      customHeaderImage
        ? getCloudflareTransformedURL(customHeaderImage) : DEFAULT_CARD_HEADER),
    [customHeaderImage, DEFAULT_CARD_HEADER],
  );
  // get logo image source
  const getLogoSrc = useCallback(
    () => (
      getCloudflareTransformedURL(schoolLogo, { ...cloudflareImageParams, quality: 75, width: 150 })),
    [schoolLogo],
  );

  const cardImgSrc = getCardImgSrc();
  const logoSrc = getLogoSrc();
  // For multiple school name line break
  const subTitleTruncateLine = (typeof subtitle === 'object' && subtitle?.length > 1) ? 2 : 1;

  // TODO: refactor this event handler to be passed into analytics prop on BaseCard instead.
  // The click event is not reliable because the page could navigate away before the call to segment completes.
  const handleSubscriptionEventHandler = () => {
    const componentProps = {
      program_uuid: uuid,
      title,
    };
    submitEvent(tracking, componentProps, 'edx.bi.subscription.pre-search-page.program.clicked');
  };

  const onCardClickHandler = shouldfirePreQuerySegmentEvent ? handleSubscriptionEventHandler : handleOnClick;

  return (
    <div className="expanded-product-card-wrapper">
      <ProspectusLink
        href={url}
        className="expanded-product-card-link"
        analytics={cardAnalytics}
      >
        <Card
          className={classNames('expanded-product-card position-relative', variant)}
          variant={variant}
          onClick={onCardClickHandler}
          isLoading={isLoading}
        >
          <Card.ImageCap
          // @todo WS-3952 Paragon fallbackSrc dones't work, this should be removed laterly
            className="expanded-product-card-image-show"
            src={cardImgSrc}
            srcAlt={`header image for ${subtitle}`}
            fallbackSrc={DEFAULT_CARD_HEADER}
            logoSrc={logoSrc}
            logoAlt={`logo for ${subtitle}`}
            imageLoadingType="lazy"
          />
          <Card.Header
            title={(
              <Truncate
                lines={3}
                trimWhitespace
                ellipsis="…"
              >
                {title}
              </Truncate>
            )}
            subtitle={(
              <Truncate lines={subTitleTruncateLine} trimWhitespace>
                {subtitle}
              </Truncate>
            )}
          />
          <Card.Section className="d-flex flex-column justify-content-end">
            {/* {priceString && (
              <div>
                <p className="h3">{priceString}</p>
              </div>
            )}
            {rating && (
              <div className="mt-3 d-flex align-items-center">
                <p className="h4 mr-2">{rating}</p>
                <StarRating rating={rating} variant={variant} className="mr-2" />
                <p className="x-small">({reviewCount} reviews)</p>
              </div>
            )} */}
            {tags?.length > 0 && (
              <div className="expanded-product-card__tags d-flex flex-wrap mt-3">
                {tags.map(tag => <Badge key={`product-card-tag-${uuid}-${tag}`}>{tag}</Badge>)}
              </div>
            )}
            <FooterList
              list={getFooterList()}
              variant={variant}
              className="mt-3"
            />
          </Card.Section>
          <div className="expanded-product-card__banner position-absolute bg-primary-300 px-3 py-1.5 w-100">
            <p className="small text-white">{productTypeCopy}</p>
          </div>
        </Card>
      </ProspectusLink>
    </div>
  );
}

export default ExpandedProductCard;
